import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from 'store';

import { getTravelDetail } from 'actions/travelRequested';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { LogicalFare } from 'models/logicalFare';
import { RepprovalReason } from 'models/repprovalReason';
import { TravelDetails as TravelDetailsModel } from 'models/travelDetails';
import TravelDetails from 'screens/components/TravelDetails';
import { amplitudelogEvent, ViewTravelReservationDetailEvent } from 'services/amplitudeService';
import { amplitudeEvents } from 'utils/amplitudeEvents';

interface Props {
  show: boolean;
  id: string;
  data?: TravelDetailsModel;
  submitting?: boolean;
  forceRefresh?: boolean;
  reasons?: RepprovalReason[];
  lowestLogicalFareList?: LogicalFare[];
  onClose: () => void;
  onConfirm?: () => void;
  onDecline?: (reason?: RepprovalReason) => void;
  onRefreshed?: () => void;
}

const AdminTravelApprovalDetails = ({ forceRefresh = false, onRefreshed, reasons, ...props }: Props) => {
  const { id, lowestLogicalFareList } = props;

  const { t } = useStoreTranslation();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);

  const { accountResult } = useSelector((state: RootState) => state.user);
  if (!accountResult) return null;

  const fetch = useCallback(async () => {
    if (!props.show || loaded || !id) return;

    setLoading(true);
    const {
      data: { data: travel },
    } = await getTravelDetail(id, accountResult?.sessionToken);
    setData({ ...travel, lowestLogicalFareList });
    setLoading(false);
    setLoaded(true);

    const amplitudeEvent: ViewTravelReservationDetailEvent = {
      SOURCE: 'APROBACIONES',
      ITINERARY_ID: parseInt(id),
    };

    amplitudelogEvent(amplitudeEvents.VIEW_TRAVEL_RESERVATION_DETAIL, amplitudeEvent, accountResult);
  }, [props.show, id, accountResult, loaded, lowestLogicalFareList]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    if (forceRefresh) {
      setLoaded(false);
      setLoading(true);
      fetch();
      if (onRefreshed) onRefreshed();
    }
  }, [fetch, forceRefresh, onRefreshed]);

  return (
    <TravelDetails
      {...props}
      loading={loading}
      title={t('adminTravelApprovalDetailsContainer.title')}
      data={data as TravelDetailsModel}
      requestId={id}
      userId={accountResult.id}
      reasons={reasons}
    />
  );
};

export default AdminTravelApprovalDetails;
